"use client";

import React from "react";
import { useSnackbar } from "notistack";
import { errorMessage } from "@/src/core/api/validation";
import { cn } from "@/src/lib/utils";
import {
  useClearRecentSearchesMutation,
  useLocationSearchQuery,
} from "@/src/shared/features/search/api/queries";
import {
  NEARBY_LABEL,
  searchService,
} from "@/src/shared/features/search/services";
import type {
  ILocationSearchHotel,
  ILocationSearchItem,
  IRecentLocationSearch,
} from "@/src/shared/features/search/types";

const PIN_TILE = "/icons/search/location_search_pin_tile.svg";
const CLEAR_ERROR = "Couldn't clear your recent searches. Please try again.";
const ROW =
  "-mx-2 flex w-[calc(100%+16px)] items-center rounded-xl px-2 text-left transition-colors [@media(hover:hover)]:hover:bg-[#F7F7F7] focus-visible:bg-[#F7F7F7] focus-visible:outline-none";

interface ILocationSearchListProps {
  /** The text typed so far (already debounced). */
  term: string;
  /** The picked place, highlighted when it shows again. */
  selectedId?: number | null;
  onLocation: (place: ILocationSearchItem) => void;
  onHotel: (hotel: ILocationSearchHotel) => void;
  onNearby: () => void;
  onRecent: (recent: IRecentLocationSearch) => void;
}

const PinTile = () => (
  <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center">
    <img src={PIN_TILE} alt="" className="h-9 w-9" />
  </span>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h3 className="text-sm font-medium leading-4 text-ink">{children}</h3>;

const ListSkeleton = () => (
  <div aria-hidden className="animate-pulse">
    <div className="mb-3 h-3.5 w-28 rounded bg-gray-200" />
    {Array.from({ length: 7 }).map((_, index) => (
      <div key={index} className="flex h-[50px] items-center">
        <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center">
          <span className="h-9 w-9 rounded-md bg-gray-200" />
        </span>
        <span
          className="h-4 rounded bg-gray-200"
          style={{ width: `${40 + ((index * 17) % 35)}%` }}
        />
      </div>
    ))}
  </div>
);

/**
 * The location box's results (`location_search.dart`). Blank: recent searches with
 * Clear, the "Nearby stays" row and the popular areas. Typed: the matching areas, then
 * the hotels whose name matched, each with its cover photo. Older results stay, dimmed,
 * until the next ones arrive.
 */
const LocationSearchList: React.FC<ILocationSearchListProps> = ({
  term,
  selectedId,
  onLocation,
  onHotel,
  onNearby,
  onRecent,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isPending, isError, isPlaceholderData, refetch } =
    useLocationSearchQuery(term);
  const clearRecent = useClearRecentSearchesMutation();

  if (isPending) return <ListSkeleton />;
  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-sm text-ink-secondary">
          We couldn&apos;t load places right now.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-sm font-semibold text-ink underline underline-offset-2"
        >
          Try again
        </button>
      </div>
    );
  }

  const { term: shownTerm, result } = data;
  const { locations, hotels, recent_searches: recent, is_nearby } = result;

  const handleClear = () =>
    clearRecent.mutate(undefined, {
      onError: (error) =>
        enqueueSnackbar(errorMessage(error, CLEAR_ERROR), {
          variant: "error",
        }),
    });

  const locationRow = (place: ILocationSearchItem) => (
    <button
      key={`location-${place.id}`}
      type="button"
      onClick={() => onLocation(place)}
      className={cn(ROW, "h-[50px]", selectedId === place.id && "bg-[#F7F7F7]")}
    >
      <PinTile />
      <span className="truncate text-base font-medium leading-5 text-ink">
        {searchService.capitalizeWords(place.name)}
      </span>
      {place.badge?.name && (
        <span className="ml-2 inline-flex h-4 shrink-0 items-center rounded-full bg-[#FCEEF5] px-1.5 text-10 font-semibold text-brand-dark">
          {place.badge.name}
        </span>
      )}
    </button>
  );

  const content = shownTerm ? (
    locations.length === 0 && hotels.length === 0 ? (
      <p className="py-10 text-center text-sm text-ink-secondary">
        No results found.
      </p>
    ) : (
      <>
        {locations.map(locationRow)}
        {hotels.length > 0 && (
          <div className={cn(locations.length > 0 && "pt-4")}>
            {hotels.map((hotel) => (
              <button
                key={`hotel-${hotel.listing_id}`}
                type="button"
                onClick={() => onHotel(hotel)}
                className={cn(ROW, "h-[50px] gap-2.5")}
              >
                <img
                  src={hotel.cover_photo || PIN_TILE}
                  alt=""
                  loading="lazy"
                  className="h-9 w-9 shrink-0 rounded-lg bg-[#F7F7F7] object-cover"
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium leading-5 text-ink">
                    {hotel.title}
                  </span>
                  <span className="truncate text-xs leading-4 text-ink-secondary">
                    {searchService.capitalizeWords(hotel.address)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </>
    )
  ) : (
    <>
      {recent.length > 0 && (
        <section aria-label="Recent searches">
          <div className="mb-4 flex items-center justify-between">
            <SectionTitle>Recent searches</SectionTitle>
            <button
              type="button"
              onClick={handleClear}
              className="text-13 leading-[14px] tracking-[0.2px] text-ink-secondary hover:text-ink hover:underline"
            >
              Clear
            </button>
          </div>
          {recent.map((item, index) => {
            const detail = searchService.recentSearchDetail(item);
            return (
              <button
                key={`${item.location_id ?? item.address_name}-${index}`}
                type="button"
                onClick={() => onRecent(item)}
                className={cn(ROW, "h-[50px]")}
              >
                <PinTile />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium leading-5 text-ink">
                    {searchService.capitalizeWords(item.address_name)}
                  </span>
                  {detail && (
                    <span className="truncate text-xs leading-4 text-ink-secondary">
                      {detail}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </section>
      )}
      {recent.length > 0 && is_nearby && <hr className="border-[#EBEBEB]" />}
      {is_nearby && (
        <button
          type="button"
          onClick={onNearby}
          className={cn(ROW, "h-[60px] gap-2")}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EEF7FE]">
            <img
              src="/icons/search/location_search_nearby.svg"
              alt=""
              className="h-6 w-6"
            />
          </span>
          <span className="flex flex-col">
            <span className="text-15 font-medium leading-5 text-ink">
              {NEARBY_LABEL}
            </span>
            <span className="mt-0.5 text-xs leading-4 text-ink-secondary">
              Find stays around you
            </span>
          </span>
        </button>
      )}
      {locations.length > 0 && (
        <section
          aria-label="Popular areas"
          className={cn((recent.length > 0 || is_nearby) && "mt-5")}
        >
          <SectionTitle>Popular areas</SectionTitle>
          <div className="mt-1">{locations.map(locationRow)}</div>
        </section>
      )}
    </>
  );

  return (
    <div
      aria-busy={isPlaceholderData}
      className={cn(
        "transition-opacity duration-200",
        isPlaceholderData && "pointer-events-none opacity-40",
      )}
    >
      {content}
    </div>
  );
};

export default LocationSearchList;
